/**
 * model — чистая логика таблицы «Реклама по уровням»
 * ====================================================
 *
 * Сервер (/api/ads/levels) отдаёт строки уровней с показами и уникальными
 * игроками по каждой группе плейсментов и по каждой колонке внутри группы,
 * плюс метаданные групп (подписи, порядок, сумма показов). Здесь — только то,
 * что зависит от переключателей виджета: какие колонки видны, что лежит в
 * ячейке (показы или доля игроков), порядок строк и шкала градиента.
 */

import { adsLevels as adsLevelsSpecs } from '@ggstat/chart-specs';

export const MODE_IMPS = 'imps';
export const MODE_PCT = 'pct';

/** Колонки-метрики перед плейсментами. `field` — поле строки ответа. */
export const METRIC_COLUMNS = adsLevelsSpecs.levels.metricColumns;

export const LEVEL_SORT_KEY = 'level';

const safeDiv = (a, b) => (b ? a / b : 0);

/**
 * Колонки плейсментов: группа, а у развёрнутой группы — ещё её колонки.
 * @param {Array} groups - data.groups ответа
 * @param {Set<string>} expanded - id развёрнутых групп
 * @param {boolean} orderByTotal - порядок групп и колонок по убыванию общего числа показов
 */
export function buildPlacementColumns(groups, expanded, orderByTotal) {
  const list = [...(groups || [])];
  if (orderByTotal) list.sort((a, b) => b.imps - a.imps);

  const columns = [];
  for (const group of list) {
    const details = [...(group.details || [])];
    if (orderByTotal) details.sort((a, b) => b.imps - a.imps);
    else details.sort((a, b) => a.label.localeCompare(b.label));

    const expandable = details.length > 1;
    const isExpanded = expandable && expanded.has(group.id);
    columns.push({
      id: `g:${group.id}`,
      kind: 'group',
      groupId: group.id,
      label: group.label,
      hint: details.map((d) => d.label).join(', '),
      expandable,
      expanded: isExpanded
    });
    if (isExpanded) {
      for (const detail of details) {
        columns.push({
          id: `d:${detail.key}`,
          kind: 'detail',
          groupId: group.id,
          detailKey: detail.key,
          label: detail.label,
          hint: `${group.label}: ${detail.key}`
        });
      }
    }
  }
  return columns;
}

function placementCell(row, column) {
  if (column.kind === 'group') return row.groups?.[column.groupId];
  return row.details?.[column.detailKey];
}

/**
 * Число в ячейке. В режиме «% игроков» — доля 0..1 от игроков уровня,
 * null, если игроков нет (делить не на что).
 */
export function cellValue(row, column, mode) {
  switch (column.kind) {
    case 'players':
      return row.players;
    case 'ratio':
      return row[column.field] ?? 0;
    case 'total':
      if (mode === MODE_PCT) return row.players ? safeDiv(row.total?.users || 0, row.players) : null;
      return row.total?.imps || 0;
    default: {
      const cell = placementCell(row, column);
      if (mode === MODE_PCT) return row.players ? safeDiv(cell?.users || 0, row.players) : null;
      return cell?.imps || 0;
    }
  }
}

/** Строки уровней в выбранном порядке. «Общее» сюда не входит — оно всегда сверху. */
export function sortRows(rows, sortKey, sortDir, columns, mode) {
  const list = [...(rows || [])];
  const dir = sortDir === 'asc' ? 1 : -1;
  if (!sortKey || sortKey === LEVEL_SORT_KEY) {
    return list.sort((a, b) => (a.level - b.level) * dir);
  }
  const column = columns.find((c) => c.id === sortKey);
  if (!column) return list.sort((a, b) => a.level - b.level);
  return list.sort((a, b) => {
    const av = cellValue(a, column, mode);
    const bv = cellValue(b, column, mode);
    if (av == null && bv == null) return a.level - b.level;
    if (av == null) return 1;
    if (bv == null) return -1;
    return av === bv ? a.level - b.level : (av - bv) * dir;
  });
}

/** Мин/макс каждой колонки по строкам уровней — шкала градиента «по столбцу». */
export function columnRanges(rows, columns, mode) {
  const ranges = new Map();
  for (const column of columns) {
    let min = Infinity;
    let max = -Infinity;
    for (const row of rows || []) {
      const v = cellValue(row, column, mode);
      if (v == null) continue;
      if (v < min) min = v;
      if (v > max) max = v;
    }
    ranges.set(column.id, Number.isFinite(min) ? { min, max } : null);
  }
  return ranges;
}

/** Интенсивность 0..1 внутри колонки; плоская колонка и пропуски — 0. */
export function gradientIntensity(value, range) {
  if (value == null || !range || range.max <= range.min) return 0;
  return Math.min(1, Math.max(0, (value - range.min) / (range.max - range.min)));
}

const intFormat = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

/** Текст ячейки. Пустые плейсменты — пустая ячейка, иначе таблица тонет в нулях. */
export function formatCell(value, column, mode) {
  if (value == null) return '—';
  if (column.kind === 'ratio') return value.toFixed(2);
  if (column.kind === 'players') return intFormat.format(value);
  if (mode === MODE_PCT) {
    if (column.kind !== 'total' && value === 0) return '';
    const pct = value * 100;
    if (pct > 0 && pct < 0.05) return '<0.1%';
    return `${pct < 9.95 && pct > 0 ? pct.toFixed(1) : pct.toFixed(0)}%`;
  }
  if (column.kind !== 'total' && value === 0) return '';
  return intFormat.format(value);
}
